import { useDialogState } from '@app/src/hooks/mui-hooks'
import useProviderType from '@app/src/hooks/useProviderType'
import { getLevelTitle } from '@app/src/pages/Assessments/Assessment/utils'
import AssessmentLearnMoreDialog from '@app/src/pages/Assessments/AssessmentCompanyPage/AssessmentLearnMoreDialog'
import type { AssessmentTemplate } from '@app/src/types/resourceExplorer'
import { AssessmentType } from '@app/src/types/resourceExplorer'
import { formatLevelLabel } from '@app/src/utils/assessmentLevelTranslations'
import { InfoOutlined, RadarOutlined } from '@mui/icons-material'
import { Avatar, Box, Button, Card, CardActionArea, Chip, Grid, Stack, Typography } from '@mui/material'
import React from 'react'
import { useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { getSelectableTargetLevels } from '../bulkCreateExpectationLogic'
import type { BulkCreateExpectationFormData } from '../types'
import { BulkCreateExpectationFormField } from '../types'

type LevelCardProps = {
  title: string
  subtitle: string
  description: string
  isSelected: boolean
  isRecommended?: boolean
  onSelect: () => void
}

const LevelCard: React.FC<LevelCardProps> = ({ title, subtitle, description, isSelected, isRecommended, onSelect }) => {
  const { formatMessage } = useIntl()
  return (
    <Card
      variant="outlined"
      sx={{
        position: 'relative',
        border: 2,
        borderColor: isSelected ? 'primary.main' : 'divider',
        borderRadius: 2,
      }}
    >
      <CardActionArea onClick={onSelect} role="radio" aria-checked={isSelected} sx={{ p: 3, height: '100%' }}>
        {isRecommended && (
          <Chip
            label={formatMessage({ id: 'bulkCreateExpectation.steps.setTarget.recommendedChip' })}
            size="small"
            sx={{ position: 'absolute', top: 12, right: 12 }}
          />
        )}
        <Stack spacing={2} alignItems="flex-start">
          <Avatar variant="circular" sx={{ bgcolor: 'success.light', width: 48, height: 48 }}>
            <RadarOutlined sx={{ color: 'action.active', fontSize: 28 }} />
          </Avatar>
          <Box>
            <Typography variant="h5">{title}</Typography>
            {subtitle && <Typography variant="subtitle1">{subtitle}</Typography>}
          </Box>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        </Stack>
      </CardActionArea>
    </Card>
  )
}

const getTemplateLevelDescription = (template: AssessmentTemplate, level: number): string => {
  const descriptions: Record<number, string | undefined> = {
    1: template.level1Description,
    2: template.level2Description,
    3: template.level3Description,
    4: template.level4Description,
    5: template.level5Description,
  }
  return descriptions[level] ?? ''
}

type SetTargetStepProps = {
  assessmentTemplate?: AssessmentTemplate
}

const SetTargetStep: React.FC<SetTargetStepProps> = ({ assessmentTemplate }) => {
  const { formatMessage } = useIntl()
  const { watch, setValue } = useFormContext<BulkCreateExpectationFormData>()
  const selectedLevel = watch(BulkCreateExpectationFormField.TargetAssessmentLevel)
  const [learnMoreOpen, openLearnMore, closeLearnMore] = useDialogState()
  const providerType = useProviderType()

  const isBaseline = assessmentTemplate?.assessmentType === AssessmentType.BaselineAssessment
  const selectableLevels = getSelectableTargetLevels(assessmentTemplate?.levels)

  const setLevel = (level: number) => {
    setValue(BulkCreateExpectationFormField.TargetAssessmentLevel, level, { shouldValidate: true })
  }

  const describeLevel = (level: number): { title: string; subtitle: string; description: string } => {
    const title = formatLevelLabel(assessmentTemplate, level, formatMessage)
    if (!assessmentTemplate) return { title, subtitle: '', description: '' }
    if (isBaseline) {
      return {
        title,
        subtitle: '',
        description: formatMessage(
          { id: `bulkCreateExpectation.steps.setTarget.level${level}Description` },
          { providerType },
        ),
      }
    }
    return {
      title,
      subtitle: getLevelTitle(level, assessmentTemplate, formatMessage),
      description: getTemplateLevelDescription(assessmentTemplate, level),
    }
  }

  return (
    <Stack spacing={3}>
      <Box role="radiogroup" aria-label={formatMessage({ id: 'bulkCreateExpectation.steps.setTarget.title' })}>
        {selectableLevels.length === 1 ? (
          <LevelCard
            {...describeLevel(selectableLevels[0])}
            isSelected={selectedLevel === selectableLevels[0]}
            isRecommended={false}
            onSelect={() => setLevel(selectableLevels[0])}
          />
        ) : (
          <Grid container spacing={3}>
            {selectableLevels.map((level, index) => (
              <Grid item xs={6} key={level}>
                <LevelCard
                  {...describeLevel(level)}
                  isSelected={selectedLevel === level}
                  isRecommended={selectableLevels.length > 1 && index === 0}
                  onSelect={() => setLevel(level)}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
      <Box>
        <Button variant="text" startIcon={<InfoOutlined />} onClick={openLearnMore} disabled={!assessmentTemplate}>
          {formatMessage(
            { id: 'bulkCreateExpectation.steps.setTarget.learnMoreLink' },
            { assessmentName: assessmentTemplate?.name ?? '' },
          )}
        </Button>
      </Box>
      {assessmentTemplate && (
        <AssessmentLearnMoreDialog
          open={learnMoreOpen}
          onClose={closeLearnMore}
          assessmentTemplate={assessmentTemplate}
        />
      )}
    </Stack>
  )
}

export default SetTargetStep
